/**
 * GLM 토큰 쿼터 위젯 — 대시보드 카드
 *
 * Hermes 에이전트가 쓰는 GLM(Z.AI) 5시간 롤링 토큰 한도 잔량 + 리셋 시각 표시.
 * 소스: stock-api /v1/internal/glm-quota (glm_quota_check.py subprocess, BE 30분 캐시)
 */
import { Card, Col, Progress, Row, Spin, Statistic, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { glmQuotaApi } from "src/api/glmQuota";

/** 남은 %에 따른 색상 — 50% 이상 정상, 20~50% 주의, 20% 미만 임박 */
const quotaColor = (pct: number) => (pct >= 50 ? "#52c41a" : pct >= 20 ? "#faad14" : "#ff4d4f");

const formatCountdown = (hours: number) => {
  if (hours >= 48) return `D-${Math.floor(hours / 24)}`;
  if (hours >= 1) return `${Math.floor(hours)}시간 ${Math.round((hours % 1) * 60)}분`;
  return `${Math.max(1, Math.round(hours * 60))}분`;
};

const GlmQuotaWidget = () => {
  // BE 30분 캐시와 맞춰 FE도 30분 stale — 폴링 부하 없음
  const q = useQuery({
    queryKey: ["glm-quota"],
    queryFn: () => glmQuotaApi.getQuota(),
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const limitList = Array.isArray(q.data?.limits) ? q.data.limits : [];
  const tokens = limitList.find((l) => l.type === "TOKENS_LIMIT");
  const time = limitList.find((l) => l.type === "TIME_LIMIT");
  const resetAt = tokens ? dayjs(tokens.next_reset_kst) : null;

  return (
    <Card
      size="small"
      title={
        <span>
          🤖 GLM 토큰
          <Tooltip title="Hermes 에이전트 GLM 5시간 롤링 한도 — 리셋 시 잔여 소멸">
            <InfoCircleOutlined style={{ marginLeft: 6, color: "#999", fontSize: 12 }} />
          </Tooltip>
        </span>
      }
      style={{ borderTop: "2px solid #eb2f96", height: "100%" }}
    >
      <Spin spinning={q.isLoading}>
        {tokens ? (
          <Row gutter={8} align="middle">
            <Col flex="none">
              <Progress
                type="dashboard"
                percent={tokens.remaining_pct}
                size={86}
                strokeColor={quotaColor(tokens.remaining_pct)}
                format={(p) => <span style={{ fontSize: 17, fontWeight: 800 }}>{p}%</span>}
              />
            </Col>
            <Col flex="auto" style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: "#999" }}>리셋</span>{" "}
                <span style={{ fontWeight: 700 }}>{resetAt?.format("HH:mm")}</span>
                <span style={{ color: "#999" }}> ({formatCountdown(tokens.hours_until_reset)})</span>
              </div>
              {time && (
                <Statistic
                  title={<span style={{ fontSize: 11 }}>월간 시간</span>}
                  value={time.remaining_pct}
                  suffix="%"
                  valueStyle={{ fontSize: 15, fontWeight: 700 }}
                />
              )}
            </Col>
          </Row>
        ) : !q.isLoading ? (
          <span style={{ color: "#999", fontSize: 12 }}>쿼터 정보를 불러올 수 없습니다</span>
        ) : (
          <div style={{ height: 86 }} />
        )}
      </Spin>
    </Card>
  );
};

export default GlmQuotaWidget;
